import React, { startTransition, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageContent, PageLayout } from '@reapitform/dashboard-ui'
import { Checkbox, InputSearch } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { AddIcon } from '@reapit/elements/icons/add'
import { FormTypeTiles } from './view/form-type-tiles'
import { FormsTable, VERSION_FILTER_PARAM, VERSION_FILTER_ALL_VALUE } from './view/forms-table'
import { CreateFormDrawer } from './view/create-form'
import { CreateVersionDrawer } from './view/create-version-drawer'
import { useAccessMode } from '../../access-mode-provider'
import { SearchContainer, Toolbar } from './style'

/**
 * The Form Builder page: form-type tiles (with counts) over a searchable grid of forms.
 * Filter + search state lives in the URL (`filterByType`, `query`) so the tiles and grid stay in sync.
 */
export const FormBuilderPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isCreateVersionOpen, setIsCreateVersionOpen] = useState(false)
  const { isReadOnly } = useAccessMode()

  const handleSearch = (value: string) => {
    startTransition(() => {
      setSearchParams((params) => {
        if (!value) params.delete('query')
        else params.set('query', value)
        return params
      })
    })
  }

  // Absence of the param means the default (current-version-only) — only an explicit 'all' value turns it off.
  const showAllVersions = searchParams.get(VERSION_FILTER_PARAM) === VERSION_FILTER_ALL_VALUE

  const handleToggleCurrentVersionOnly = (checked: boolean) => {
    startTransition(() => {
      setSearchParams((params) => {
        if (checked) params.delete(VERSION_FILTER_PARAM)
        else params.set(VERSION_FILTER_PARAM, VERSION_FILTER_ALL_VALUE)
        return params
      })
    })
  }

  return (
    <PageLayout pageTitle="Form Builder">
      <PageContent>
        <FormTypeTiles />
        <Toolbar>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <SearchContainer>
              <InputSearch
                placeholder="Search forms"
                initialSearchValue={searchParams.get('query') ?? ''}
                onChange={handleSearch}
              />
            </SearchContainer>
            <Checkbox
              label="Show only current version"
              checked={!showAllVersions}
              onChange={(e) => handleToggleCurrentVersionOnly(e.target.checked)}
            />
          </div>
          {!isReadOnly && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button
                variant="secondary"
                size="medium"
                iconLeft={<AddIcon />}
                onClick={() => setIsCreateVersionOpen(true)}
                type="button"
              >
                Create Version
              </Button>
              <Button
                variant="primary"
                size="medium"
                iconLeft={<AddIcon />}
                onClick={() => setIsCreateOpen(true)}
                type="button"
              >
                Create Form
              </Button>
            </div>
          )}
        </Toolbar>
        <FormsTable />
      </PageContent>
      <CreateFormDrawer isOpen={isCreateOpen} onOpenChange={setIsCreateOpen} />
      <CreateVersionDrawer isOpen={isCreateVersionOpen} onOpenChange={setIsCreateVersionOpen} />
    </PageLayout>
  )
}

export default FormBuilderPage
